// @ts-nocheck
import {
  computed,
  defineComponent,
  getCurrentInstance,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  resolveDynamicComponent,
  shallowRef,
  toRef,
  unref,
  watch,
  withMemo,
} from 'vue'
import {
  getScrollBarWidth,
  hasOwn,
  isClient,
  isNumber,
  isString,
} from '@jdesign-vue/utils'
import { useNamespace } from '@jdesign-vue/hooks'
import Scrollbar from '../components/scrollbar'
import { useGridWheel } from '../hooks/use-grid-wheel'
import { useCache } from '../hooks/use-cache'
import { virtualizedGridProps } from '../props'
import { getRTLOffsetType, getScrollDir, isRTL } from '../utils'
import {
  AUTO_ALIGNMENT,
  BACKWARD,
  FORWARD,
  ITEM_RENDER_EVT,
  RTL,
  RTL_OFFSET_NAG,
  RTL_OFFSET_POS_ASC,
  RTL_OFFSET_POS_DESC,
  SCROLL_EVT,
} from '../defaults'
import type {
  CSSProperties,
  Ref,
  StyleValue,
  UnwrapRef,
  VNode,
  VNodeChild,
} from 'vue'
import type {
  Alignment,
  GridConstructorProps,
  GridScrollOptions,
  ScrollbarExpose,
} from '../types'
import type { VirtualizedGridProps } from '../props'

const createGrid = ({
  name,
  clearCache,
  getColumnPosition,
  getColumnStartIndexForOffset,
  getColumnStopIndexForStartIndex,
  getEstimatedTotalHeight,
  getEstimatedTotalWidth,
  getColumnOffset,
  getRowOffset,
  getRowPosition,
  getRowStartIndexForOffset,
  getRowStopIndexForStartIndex,

  initCache,
  injectToInstance,
  validateProps,
}: GridConstructorProps<VirtualizedGridProps>) => {
  return defineComponent({
    name: name ?? 'JdVirtualList',
    props: virtualizedGridProps,
    emits: [ITEM_RENDER_EVT, SCROLL_EVT],
    setup(props, { emit, expose, slots }) {
      const ns = useNamespace('vl')

      validateProps(props)
      const instance = getCurrentInstance()!
      const cache = ref(initCache(props, instance))
      injectToInstance?.(instance, cache)
      // refs
      // here windowRef and innerRef can be type of HTMLElement
      // or user defined component type, depends on the type passed
      // by user
      const windowRef = ref<HTMLElement>()
      const hScrollbar = ref<ScrollbarExpose>()
      const vScrollbar = ref<ScrollbarExpose>()
      // innerRef is the actual container element which contains all the elements
      const innerRef = ref(null)
      // 把单一 ref 对象拆成扁平的 shallowRef，避免水平滚动触发垂直依赖、反之亦然。
      // 收益：垂直滚动只触发 rowsToRender、yAxisScrollDir/scrollTop 相关的 edge，
      // 不会再让 columnsToRender / 横向 edge / windowStyle 这些无关 computed 失效。
      const isScrollingRef = shallowRef(false)
      const updateRequestedRef = shallowRef(false)
      const scrollLeftRef = shallowRef(
        isNumber(props.initScrollLeft) ? (props.initScrollLeft as number) : 0
      )
      const scrollTopRef = shallowRef(
        isNumber(props.initScrollTop) ? (props.initScrollTop as number) : 0
      )
      const xAxisScrollDirRef = shallowRef(FORWARD)
      const yAxisScrollDirRef = shallowRef(FORWARD)

      // 对外暴露的 states 仍然是 ref-like，但读取时聚合各扁平 ref。
      // 使用 computed 会聚合所有依赖，调用方在 scrollTop 变化时也会重算横向相关 - 这是
      // 调用方需自行管理的代价；内部不再走 states，避免无关响应式触发。
      const states = computed(() => ({
        isScrolling: isScrollingRef.value,
        scrollLeft: scrollLeftRef.value,
        scrollTop: scrollTopRef.value,
        updateRequested: updateRequestedRef.value,
        xAxisScrollDir: xAxisScrollDirRef.value,
        yAxisScrollDir: yAxisScrollDirRef.value,
      }))
      let resetIsScrollingTimer: ReturnType<typeof setTimeout> | null = null
      let lastRenderedRange: string | null = null

      const getItemStyleCache = useCache()

      // computed
      const parsedHeight = computed(() =>
        Number.parseInt(`${props.height}`, 10)
      )
      const parsedWidth = computed(() => Number.parseInt(`${props.width}`, 10))
      const columnsToRender = computed(() => {
        const { totalColumn, totalRow, columnCache } = props
        const scrollLeft = scrollLeftRef.value

        if (totalColumn === 0 || totalRow === 0) {
          return [0, 0, 0, 0]
        }

        const startIndex = getColumnStartIndexForOffset(
          props,
          scrollLeft,
          unref(cache)
        )
        const stopIndex = getColumnStopIndexForStartIndex(
          props,
          startIndex,
          scrollLeft,
          unref(cache)
        )

        // 滚动期间不再缩小 buffer：isScrolling 在 true/false 间切换会导致
        // 可视行范围来回变化，反而增加 mount/unmount 与样式动画开销。
        const cacheBackward = Math.max(1, columnCache)
        const cacheForward = Math.max(1, columnCache)

        return [
          Math.max(0, startIndex - cacheBackward),
          Math.max(0, Math.min(totalColumn! - 1, stopIndex + cacheForward)),
          startIndex,
          stopIndex,
        ]
      })

      const rowsToRender = computed(() => {
        const { totalColumn, totalRow, rowCache } = props
        const scrollTop = scrollTopRef.value

        if (totalColumn === 0 || totalRow === 0) {
          return [0, 0, 0, 0]
        }

        const startIndex = getRowStartIndexForOffset(
          props,
          scrollTop,
          unref(cache)
        )
        const stopIndex = getRowStopIndexForStartIndex(
          props,
          startIndex,
          scrollTop,
          unref(cache)
        )

        const cacheBackward = Math.max(1, rowCache)
        const cacheForward = Math.max(1, rowCache)

        return [
          Math.max(0, startIndex - cacheBackward),
          Math.max(0, Math.min(totalRow! - 1, stopIndex + cacheForward)),
          startIndex,
          stopIndex,
        ]
      })

      const estimatedTotalHeight = computed(() => {
        // 明确依赖 props.totalRow，确保行数变化时重新计算
        const totalRow = props.totalRow
        return getEstimatedTotalHeight({ ...props, totalRow }, unref(cache))
      })
      const estimatedTotalWidth = computed(() =>
        getEstimatedTotalWidth(props, unref(cache))
      )

      const windowStyle = computed<StyleValue>(() => [
        {
          position: 'relative',
          overflow: 'hidden',
          WebkitOverflowScrolling: 'touch',
          willChange: 'transform',
        },
        {
          direction: props.direction,
          height: isNumber(props.height) ? `${props.height}px` : props.height,
          width: isNumber(props.width) ? `${props.width}px` : props.width,
        },
        props.style ?? {},
      ])

      const innerStyle = computed(() => {
        const width = `${unref(estimatedTotalWidth)}px`
        const height = `${unref(estimatedTotalHeight)}px`

        // 旧实现里加 `pointerEvents: 'none'` 是为了屏蔽滚动中 hover，但代价是每次
        // isScrolling 切换都会让 inner DOM patch（重写 style），收益甚微。
        // 滚动中的 hover 高亮已经在 SCSS 里通过 is-scrolling 类禁用了。
        return {
          height,
          width,
        }
      })

      // methods
      const emitEvents = () => {
        const { totalColumn, totalRow } = props

        if (totalColumn! > 0 && totalRow! > 0) {
          const [
            columnCacheStart,
            columnCacheEnd,
            columnVisibleStart,
            columnVisibleEnd,
          ] = unref(columnsToRender)
          const [rowCacheStart, rowCacheEnd, rowVisibleStart, rowVisibleEnd] =
            unref(rowsToRender)
          const renderedRange = [
            columnCacheStart,
            columnCacheEnd,
            columnVisibleStart,
            columnVisibleEnd,
            rowCacheStart,
            rowCacheEnd,
            rowVisibleStart,
            rowVisibleEnd,
          ].join(',')

          if (renderedRange !== lastRenderedRange) {
            lastRenderedRange = renderedRange
            // emit the render item event with
            // [xAxisInvisibleStart, xAxisInvisibleEnd, xAxisVisibleStart, xAxisVisibleEnd]
            // [yAxisInvisibleStart, yAxisInvisibleEnd, yAxisVisibleStart, yAxisVisibleEnd]
            emit(ITEM_RENDER_EVT, {
              columnCacheStart,
              columnCacheEnd,
              rowCacheStart,
              rowCacheEnd,
              columnVisibleStart,
              columnVisibleEnd,
              rowVisibleStart,
              rowVisibleEnd,
            })
          }
        }

        emit(SCROLL_EVT, {
          xAxisScrollDir: xAxisScrollDirRef.value,
          scrollLeft: scrollLeftRef.value,
          yAxisScrollDir: yAxisScrollDirRef.value,
          scrollTop: scrollTopRef.value,
          updateRequested: updateRequestedRef.value,
        })
      }

      const onScroll = (e: Event) => {
        const {
          clientHeight,
          clientWidth,
          scrollHeight,
          scrollLeft,
          scrollTop,
          scrollWidth,
        } = e.currentTarget as HTMLElement

        const prevScrollLeft = scrollLeftRef.value
        const prevScrollTop = scrollTopRef.value

        if (prevScrollTop === scrollTop && prevScrollLeft === scrollLeft) {
          return
        }

        let _scrollLeft = scrollLeft

        if (isRTL(props.direction)) {
          switch (getRTLOffsetType()) {
            case RTL_OFFSET_NAG:
              _scrollLeft = -scrollLeft
              break
            case RTL_OFFSET_POS_DESC:
              _scrollLeft = scrollWidth - clientWidth - scrollLeft
              break
          }
        }

        const clampedTop = Math.max(
          0,
          Math.min(scrollTop, scrollHeight - clientHeight)
        )

        // 按字段差异更新各自的 ref，避免对未变化的轴触发响应式依赖
        if (!isScrollingRef.value) isScrollingRef.value = true
        if (updateRequestedRef.value) updateRequestedRef.value = false
        if (prevScrollLeft !== _scrollLeft) {
          scrollLeftRef.value = _scrollLeft
          xAxisScrollDirRef.value = getScrollDir(prevScrollLeft, _scrollLeft)
        }
        if (prevScrollTop !== clampedTop) {
          scrollTopRef.value = clampedTop
          yAxisScrollDirRef.value = getScrollDir(prevScrollTop, clampedTop)
        }

        scheduleResetIsScrolling()

        // 仅当 updateRequested=true（即程序主动 scrollTo）时才需要 onUpdated()
        emitEvents()
      }

      const onVerticalScroll = (distance: number, totalSteps: number) => {
        const height = unref(parsedHeight)
        const offset =
          ((estimatedTotalHeight.value - height) / totalSteps) * distance
        scrollTo({
          scrollTop: Math.min(estimatedTotalHeight.value - height, offset),
        })
      }

      const onHorizontalScroll = (distance: number, totalSteps: number) => {
        const width = unref(parsedWidth)
        const offset =
          ((estimatedTotalWidth.value - width) / totalSteps) * distance
        scrollTo({
          scrollLeft: Math.min(estimatedTotalWidth.value - width, offset),
        })
      }

      const { onWheel } = useGridWheel(
        {
          atXStartEdge: computed(() => scrollLeftRef.value <= 0),
          atXEndEdge: computed(
            () =>
              scrollLeftRef.value >=
              estimatedTotalWidth.value - unref(parsedWidth)
          ),
          atYStartEdge: computed(() => scrollTopRef.value <= 0),
          atYEndEdge: computed(
            () =>
              scrollTopRef.value >=
              estimatedTotalHeight.value - unref(parsedHeight)
          ),
        },
        (x: number, y: number) => {
          hScrollbar.value?.onMouseUp?.()
          vScrollbar.value?.onMouseUp?.()
          const width = unref(parsedWidth)
          const height = unref(parsedHeight)
          scrollTo({
            scrollLeft: Math.min(
              scrollLeftRef.value + x,
              estimatedTotalWidth.value - width
            ),
            scrollTop: Math.min(
              scrollTopRef.value + y,
              estimatedTotalHeight.value - height
            ),
          })
        }
      )

      const scrollTo = ({
        scrollLeft = scrollLeftRef.value,
        scrollTop = scrollTopRef.value,
      }: GridScrollOptions) => {
        scrollLeft = Math.max(scrollLeft, 0)
        scrollTop = Math.max(scrollTop, 0)
        const prevScrollLeft = scrollLeftRef.value
        const prevScrollTop = scrollTopRef.value
        if (scrollTop === prevScrollTop && scrollLeft === prevScrollLeft) {
          return
        }

        if (!updateRequestedRef.value) updateRequestedRef.value = true
        if (prevScrollLeft !== scrollLeft) {
          scrollLeftRef.value = scrollLeft
          xAxisScrollDirRef.value = getScrollDir(prevScrollLeft, scrollLeft)
        }
        if (prevScrollTop !== scrollTop) {
          scrollTopRef.value = scrollTop
          yAxisScrollDirRef.value = getScrollDir(prevScrollTop, scrollTop)
        }

        scheduleResetIsScrolling()

        onUpdated()
        emitEvents()
      }

      const scrollToItem = (
        rowIndex = 0,
        columnIdx = 0,
        alignment: Alignment = AUTO_ALIGNMENT
      ) => {
        columnIdx = Math.max(0, Math.min(columnIdx, props.totalColumn! - 1))
        rowIndex = Math.max(0, Math.min(rowIndex, props.totalRow! - 1))
        const scrollBarWidth = getScrollBarWidth(ns.namespace.value)

        const _cache = unref(cache)
        const estimatedHeight = getEstimatedTotalHeight(props, _cache)
        const estimatedWidth = getEstimatedTotalWidth(props, _cache)

        scrollTo({
          scrollLeft: getColumnOffset(
            props,
            columnIdx,
            alignment,
            scrollLeftRef.value,
            _cache,
            estimatedWidth > props.width! ? scrollBarWidth : 0
          ),
          scrollTop: getRowOffset(
            props,
            rowIndex,
            alignment,
            scrollTopRef.value,
            _cache,
            estimatedHeight > props.height! ? scrollBarWidth : 0
          ),
        })
      }

      const getItemStyle = (
        rowIndex: number,
        columnIndex: number
      ): CSSProperties => {
        const { columnWidth, direction, rowHeight } = props
        const itemStyleCache = getItemStyleCache.value(
          clearCache && columnWidth,
          clearCache && rowHeight,
          clearCache && direction
        )
        // since there was no need to introduce an nested array into cache object
        // we use row,column to construct the key for indexing the map.
        const key = `${rowIndex},${columnIndex}`

        if (hasOwn(itemStyleCache, key)) {
          return itemStyleCache[key]
        } else {
          const [, left] = getColumnPosition(props, columnIndex, unref(cache))
          const _cache = unref(cache)

          const rtl = isRTL(direction)
          const [height, top] = getRowPosition(props, rowIndex, _cache)
          const [width] = getColumnPosition(props, columnIndex, _cache)

          itemStyleCache[key] = {
            position: 'absolute',
            left: rtl ? undefined : `${left}px`,
            right: rtl ? `${left}px` : undefined,
            top: `${top}px`,
            height: `${height}px`,
            width: `${width}px`,
          }

          return itemStyleCache[key]
        }
      }

      // Keep isScrolling stable during continuous scroll. Resetting on nextTick
      // makes the rendered range and style cache oscillate on every scroll event.
      const scheduleResetIsScrolling = () => {
        if (resetIsScrollingTimer) clearTimeout(resetIsScrollingTimer)
        resetIsScrollingTimer = setTimeout(resetIsScrolling, 150)
      }

      const resetIsScrolling = () => {
        resetIsScrollingTimer = null
        isScrollingRef.value = false
        if (!props.perfMode) {
          nextTick(() => {
            getItemStyleCache.value(-1, null, null)
          })
        }
      }

      // life cycles
      onMounted(() => {
        // for SSR
        if (!isClient) return
        const { initScrollLeft, initScrollTop } = props
        const windowElement = unref(windowRef)
        if (windowElement) {
          if (isNumber(initScrollLeft)) {
            windowElement.scrollLeft = initScrollLeft
          }
          if (isNumber(initScrollTop)) {
            windowElement.scrollTop = initScrollTop
          }
        }
        emitEvents()
      })

      onBeforeUnmount(() => {
        if (resetIsScrollingTimer) {
          clearTimeout(resetIsScrollingTimer)
          resetIsScrollingTimer = null
        }
      })

      const onUpdated = () => {
        const { direction } = props
        const scrollLeft = scrollLeftRef.value
        const scrollTop = scrollTopRef.value
        const updateRequested = updateRequestedRef.value

        const windowElement = unref(windowRef)
        if (updateRequested && windowElement) {
          if (direction === RTL) {
            switch (getRTLOffsetType()) {
              case RTL_OFFSET_NAG: {
                windowElement.scrollLeft = -scrollLeft
                break
              }
              case RTL_OFFSET_POS_ASC: {
                windowElement.scrollLeft = scrollLeft
                break
              }
              default: {
                const { clientWidth, scrollWidth } = windowElement
                windowElement.scrollLeft =
                  scrollWidth - clientWidth - scrollLeft
                break
              }
            }
          } else {
            windowElement.scrollLeft = Math.max(0, scrollLeft)
          }

          windowElement.scrollTop = Math.max(0, scrollTop)
        }
      }

      const { resetAfterColumnIndex, resetAfterRowIndex, resetAfter } =
        instance.proxy as any

      expose({
        windowRef,
        innerRef,
        getItemStyleCache,
        scrollTo,
        scrollToItem,
        states,
        resetAfterColumnIndex,
        resetAfterRowIndex,
        resetAfter,
      })

      // rendering part

      const renderScrollbars = () => {
        const {
          scrollbarAlwaysOn,
          scrollbarStartGap,
          scrollbarEndGap,
          totalColumn,
          totalRow,
        } = props

        const width = unref(parsedWidth)
        const height = unref(parsedHeight)
        const estimatedWidth = unref(estimatedTotalWidth)
        const estimatedHeight = unref(estimatedTotalHeight)
        const scrollLeft = scrollLeftRef.value
        const scrollTop = scrollTopRef.value
        const horizontalScrollbar = h(Scrollbar, {
          ref: hScrollbar,
          alwaysOn: scrollbarAlwaysOn,
          startGap: scrollbarStartGap,
          endGap: scrollbarEndGap,
          class: ns.e('horizontal'),
          clientSize: width,
          layout: 'horizontal',
          onScroll: onHorizontalScroll,
          ratio: (width * 100) / estimatedWidth,
          scrollFrom: scrollLeft / (estimatedWidth - width),
          total: totalRow,
          visible: true,
        })

        const verticalScrollbar = h(Scrollbar, {
          ref: vScrollbar,
          alwaysOn: scrollbarAlwaysOn,
          startGap: scrollbarStartGap,
          endGap: scrollbarEndGap,
          class: ns.e('vertical'),
          clientSize: height,
          layout: 'vertical',
          onScroll: onVerticalScroll,
          ratio: (height * 100) / estimatedHeight,
          scrollFrom: scrollTop / (estimatedHeight - height),

          total: totalColumn,
          visible: true,
        })

        return {
          horizontalScrollbar,
          verticalScrollbar,
        }
      }

      // v-memo / withMemo 的 vnode 缓存：按 (rowIndex * totalColumn + columnIndex)
      // 索引到 cache 中的位置。数据/列变化时整个清空（见下面 watchers）。
      //
      // 这是真正彻底的优化：当 memo deps（rowData、style、isScrolling 等）引用都没变时，
      // withMemo 直接返回上次的 VNode —— Vue diff 在父层比较 children 时看到相同 VNode
      // 引用会跳过整个 patch 子树（不仅是 row 自身的 update，连 patchProps、cell diff
      // 全部跳过）。这是 react-window / react-virtual 同等量级的优化。
      const itemVNodeCache: any[] = []
      const clearItemCache = () => {
        itemVNodeCache.length = 0
      }

      // 任何会改变 row vnode 内容的 prop 变化都要清缓存：
      // - data 数组替换 → row 内容变
      // - columns/rowHeight/columnWidth → cell style 变（getItemStyleCache 自己也会清）
      // - direction → 布局方向
      // - totalColumn 变化 → cache index 计算变（row * totalColumn + column）
      // - totalRow 变化 → 行数变化（展开/折叠）
      watch(
        [
          toRef(props, 'data'),
          toRef(props, 'columnWidth'),
          toRef(props, 'rowHeight'),
          toRef(props, 'direction'),
          toRef(props, 'totalColumn'),
          toRef(props, 'totalRow'),
        ],
        clearItemCache
      )

      // 监听 totalRow 变化，清除超出范围的缓存
      watch(toRef(props, 'totalRow'), (newTotal, oldTotal) => {
        if (newTotal !== oldTotal) {
          // 行数变化时，重置缓存
          const grid = instance.proxy as any
          if (grid?.resetAllCache) {
            grid.resetAllCache()
          }
        }
      })

      const renderItems = () => {
        const [columnStart, columnEnd] = unref(columnsToRender)
        const [rowStart, rowEnd] = unref(rowsToRender)
        const { data, totalColumn, totalRow, useIsScrolling, itemKey } = props
        const children: VNodeChild[] = []
        if (totalRow > 0 && totalColumn > 0) {
          const isScrolling = useIsScrolling
            ? isScrollingRef.value
            : undefined
          for (let row = rowStart; row <= rowEnd; row++) {
            const rowData = data?.[row]
            for (let column = columnStart; column <= columnEnd; column++) {
              const cacheIndex = row * totalColumn + column
              const style = getItemStyle(row, column)
              const key = itemKey({
                columnIndex: column,
                data,
                rowIndex: row,
              })
              // memo deps：覆盖 slot 渲染所依赖的全部输入
              // - rowData：行数据
              // - style：cell 位置 / 尺寸
              // - isScrolling：仅在 useIsScrolling=true 时是 boolean，否则 undefined
              // - data：data 数组引用，data 整体替换时强制失效
              const memo = [rowData, style, isScrolling, data]
              children.push(
                withMemo(
                  memo,
                  () =>
                    slots.default?.({
                      columnIndex: column,
                      data,
                      key,
                      isScrolling,
                      style,
                      rowIndex: row,
                    }) as any,
                  itemVNodeCache,
                  cacheIndex
                )
              )
            }
          }
        }
        return children
      }

      const renderInner = () => {
        const Inner = resolveDynamicComponent(props.innerElement) as VNode
        const children = renderItems()
        return [
          h(
            Inner,
            {
              style: unref(innerStyle),
              ref: innerRef,
            },
            !isString(Inner)
              ? {
                  default: () => children,
                }
              : children
          ),
        ]
      }

      const renderWindow = () => {
        const Container = resolveDynamicComponent(
          props.containerElement
        ) as VNode
        const { horizontalScrollbar, verticalScrollbar } = renderScrollbars()
        const Inner = renderInner()

        return h(
          'div',
          {
            key: 0,
            class: ns.e('wrapper'),
            role: props.role,
          },
          [
            h(
              Container,
              {
                class: props.className,
                style: unref(windowStyle),
                onScroll,
                onWheel,
                ref: windowRef,
              },
              !isString(Container) ? { default: () => Inner } : Inner
            ),
            horizontalScrollbar,
            verticalScrollbar,
          ]
        )
      }

      return renderWindow
    },
  })
}

export default createGrid

type Dir = typeof FORWARD | typeof BACKWARD

export type GridInstance = InstanceType<ReturnType<typeof createGrid>> &
  UnwrapRef<{
    windowRef: Ref<HTMLElement>
    innerRef: Ref<HTMLElement>
    getItemStyleCache: ReturnType<typeof useCache>
    scrollTo: (scrollOptions: GridScrollOptions) => void
    scrollToItem: (
      rowIndex: number,
      columnIndex: number,
      alignment: Alignment
    ) => void
    states: Ref<{
      isScrolling: boolean
      scrollLeft: number
      scrollTop: number
      updateRequested: boolean
      xAxisScrollDir: Dir
      yAxisScrollDir: Dir
    }>
  }>
